import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, RefreshCw, Send, FileText, ExternalLink, Pencil, Trash2, RotateCcw, Check, Wallet } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import api from '../../services/api';
import { useDialogs } from '../../components/Dialogs';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';
import { Message } from '../time/ui';
import { ExitSteps } from './exitUi';
import { longDate, dateWithYear, viewUrl } from './docUtils';

// HR → Final settlement, and the approvers' view of it: what is paid to the
// person and what is recovered, with the reasons for any change, the approval
// trail, payment, and the letters that follow.

const STATUS = {
    DRAFT: ['Draft', '#d97706'], PENDING_APPROVAL: ['Waiting for approval', '#8b5cf6'], APPROVED: ['Approved', '#10b981'],
    PAID: ['Settled', '#3b82f6'], CLOSED: ['Closed', '#6b7280'], CANCELLED: ['Cancelled', '#6b7280'],
};
const MODES = [['BANK_TRANSFER', 'Bank transfer'], ['CASH', 'Cash'], ['CHEQUE', 'Cheque'], ['OTHER', 'Other'], ['RECOVERY', 'Recovery received']];

const money = (n, currency) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(Number(n) || 0);

const LineRow = ({ l, s, editable, onChanged, say }) => {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState(String(l.amount));
    const [reason, setReason] = useState(l.overrideReason || '');
    const [label, setLabel] = useState(l.label);
    const [busy, setBusy] = useState(false);
    const manual = l.source === 'MANUAL';
    const run = async (fn, ok) => {
        setBusy(true);
        try { await fn(); say(true, ok); setOpen(false); onChanged(); } catch (e) { say(false, errorText(e, 'Could not change the line')); onChanged(); } finally { setBusy(false); }
    };
    const save = (e) => {
        e.preventDefault();
        run(() => api.patch(`/lifecycle/settlements/${s.id}/lines/${l.id}`, manual ? { amount: Number(amount), label } : { amount: Number(amount), reason }), 'Saved');
    };
    return (
        <li key={`${l.id}-${l.amount}-${l.overrideReason}`} style={{ borderTop: rowBorder, padding: '8px 0' }} data-testid={`line-${l.code}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ color: 'var(--text-main)' }}>
                    {l.label}
                    {l.quantity && l.rate ? <div style={muted}>{l.quantity} days at {money(l.rate, s.currency)} a day</div> : null}
                    {l.overrideAmount !== null && <div style={muted}>Changed from {money(l.computedAmount, s.currency)}: {l.overrideReason}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <strong style={{ color: 'var(--text-main)' }}>{money(l.amount, s.currency)}</strong>
                    {editable && l.kind !== 'INFO' && <button style={ghost} onClick={() => setOpen(!open)} aria-expanded={open} aria-label={`Change ${l.label}`}><Pencil size={14} /></button>}
                    {editable && manual && <button style={ghost} aria-label={`Remove ${l.label}`} disabled={busy} onClick={() => run(() => api.delete(`/lifecycle/settlements/${s.id}/lines/${l.id}`), 'Line removed')}><Trash2 size={14} /></button>}
                </div>
            </div>
            {open && (
                <form onSubmit={save} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
                    {manual && <label style={{ display: 'grid', gap: 4, flex: '1 1 200px' }}><span style={muted}>Description</span><input aria-label="Line description" maxLength={120} style={field} value={label} onChange={e => setLabel(e.target.value)} /></label>}
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Amount</span><input aria-label="New amount" type="number" min="0" step="0.01" style={{ ...field, width: 140 }} value={amount} onChange={e => setAmount(e.target.value)} /></label>
                    {!manual && <label style={{ display: 'grid', gap: 4, flex: '1 1 240px' }}><span style={muted}>Why is it different? (kept on the record)</span><input aria-label="Reason for the change" maxLength={500} style={field} value={reason} onChange={e => setReason(e.target.value)} /></label>}
                    <button className="btn btn-primary" style={primary} disabled={busy}>Save</button>
                    {!manual && l.overrideAmount !== null && <button type="button" style={ghost} disabled={busy} onClick={() => run(() => api.patch(`/lifecycle/settlements/${s.id}/lines/${l.id}`, { amount: null }), 'Back to the calculated amount')}>Use the calculated amount</button>}
                </form>
            )}
        </li>
    );
};

const AddLine = ({ s, onChanged, say }) => {
    const [f, setF] = useState({ kind: 'EARNING', label: '', amount: '' });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await api.post(`/lifecycle/settlements/${s.id}/lines`, { ...f, amount: Number(f.amount) }); say(true, `Added ${f.label}`); setF({ kind: 'EARNING', label: '', amount: '' }); onChanged(); } catch (err) { say(false, errorText(err, 'Could not add the line')); } finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }} aria-label="Add a line">
            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Add</span>
                <select aria-label="Kind of line" style={field} value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })}>
                    <option value="EARNING">An amount paid to them</option><option value="DEDUCTION">An amount recovered from them</option>
                </select>
            </label>
            <label style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}><span style={muted}>Description (gratuity, advance, bonus…)</span><input aria-label="New line description" required maxLength={120} style={field} value={f.label} onChange={e => setF({ ...f, label: e.target.value })} /></label>
            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Amount</span><input aria-label="New line amount" required type="number" min="0" step="0.01" style={{ ...field, width: 140 }} value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} /></label>
            <button style={ghost} disabled={busy}><Plus size={14} /> Add line</button>
        </form>
    );
};

const Decide = ({ s, onChanged, say }) => {
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const go = async (decision) => {
        setBusy(true);
        try {
            const r = await api.post(`/lifecycle/settlements/${s.id}/decide`, { decision, comment });
            say(r.data.statementIssued !== false, decision === 'REJECT' ? 'Sent back to HR' : r.data.outcome === 'APPROVED' ? (r.data.statementIssued === false ? 'Approved, but the statement could not be issued. HR has been told and can issue it again.' : 'Approved. The statement has been issued.') : r.data.outcome === 'STUCK' ? 'Approved, but the next step has nobody who can approve it. HR has been told.' : 'Approved. The next approver has been asked.');
            setComment('');
        } catch (e) { say(false, errorText(e, 'Could not record your decision')); } finally { setBusy(false); onChanged(); }
    };
    return (
        <div style={{ display: 'grid', gap: 8, borderTop: rowBorder, paddingTop: 12 }}>
            <strong style={{ color: 'var(--text-main)' }}>Your decision</strong>
            <input aria-label="Comment on the settlement" placeholder="Comment (needed to send it back)" maxLength={1000} style={field} value={comment} onChange={e => setComment(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={primary} disabled={busy} onClick={() => go('APPROVE')}><Check size={14} /> Approve</button>
                <button style={ghost} disabled={busy} onClick={() => go('REJECT')}><RotateCcw size={14} /> Send back</button>
            </div>
        </div>
    );
};

const Pay = ({ s, onChanged, say }) => {
    const recover = s.direction === 'RECOVERABLE';
    const [f, setF] = useState({ mode: recover ? 'RECOVERY' : 'BANK_TRANSFER', paidOn: '', reference: '' });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await api.post(`/lifecycle/settlements/${s.id}/pay`, { ...f, paidOn: f.paidOn || undefined }); say(true, recover ? 'Recorded as recovered' : 'Recorded as paid'); } catch (err) { say(false, errorText(err, 'Could not record the payment')); } finally { setBusy(false); onChanged(); }
    };
    return (
        <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: rowBorder, paddingTop: 12 }} aria-label="Record payment">
            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>{recover ? 'How it was recovered' : 'How it was paid'}</span>
                <select aria-label="Payment mode" style={field} value={f.mode} onChange={e => setF({ ...f, mode: e.target.value })}>{MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            </label>
            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Date (today if empty)</span><input aria-label="Payment date" type="date" style={field} value={f.paidOn} onChange={e => setF({ ...f, paidOn: e.target.value })} /></label>
            <label style={{ display: 'grid', gap: 4, flex: '1 1 200px' }}><span style={muted}>Reference{recover ? ' (required)' : ''}</span><input aria-label="Payment reference" maxLength={100} required={recover} style={field} value={f.reference} onChange={e => setF({ ...f, reference: e.target.value })} /></label>
            <button className="btn btn-primary" style={primary} disabled={busy}><Wallet size={14} /> {recover ? 'Record recovery' : 'Record payment'}</button>
        </form>
    );
};

const Letters = ({ s, onChanged, say }) => {
    const [f, setF] = useState({ signatoryName: '', signatoryTitle: '' });
    const [busy, setBusy] = useState(false);
    const issue = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post(`/lifecycle/settlements/${s.id}/letters`, { extra: f });
            say(true, r.data.issued.length ? `Issued and emailed: ${r.data.issued.join(' and ')}` : r.data.skipped.join('. '));
        } catch (err) { say(false, errorText(err, 'Could not issue the letters')); } finally { setBusy(false); onChanged(); }
    };
    const both = s.relievingDocId && s.experienceDocId;
    return (
        <div style={{ display: 'grid', gap: 8, borderTop: rowBorder, paddingTop: 12 }}>
            <strong style={{ color: 'var(--text-main)' }}>Relieving and experience letters</strong>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {s.relievingDocId && <a style={ghost} href={viewUrl(s.relievingDocId)} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Relieving letter</a>}
                {s.experienceDocId && <a style={ghost} href={viewUrl(s.experienceDocId)} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Experience letter</a>}
            </div>
            {!both && (
                <form onSubmit={issue} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Signed by</span><input aria-label="Signatory name" maxLength={100} style={field} value={f.signatoryName} onChange={e => setF({ ...f, signatoryName: e.target.value })} /></label>
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Title</span><input aria-label="Signatory title" maxLength={100} style={field} value={f.signatoryTitle} onChange={e => setF({ ...f, signatoryTitle: e.target.value })} /></label>
                    <button className="btn btn-primary" style={primary} disabled={busy}><FileText size={14} /> Issue the letters</button>
                </form>
            )}
            <p style={{ ...muted, margin: 0 }}>They are emailed to the person's personal and work addresses, since they cannot sign in once their account closes.</p>
        </div>
    );
};

const SettlementPage = () => {
    const { id } = useParams();
    const { ask, dialogs } = useDialogs();
    const [s, setS] = useState(null);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const say = useCallback((ok, text) => setMessage({ ok, text }), []);
    const load = useCallback(() => api.get(`/lifecycle/settlements/${id}`).then(r => setS(r.data)).catch(e => say(false, errorText(e, 'Could not load the settlement'))), [id, say]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, ok) => {
        setBusy(true);
        try { await fn(); say(true, ok); } catch (e) { say(false, errorText(e, 'That did not work')); } finally { setBusy(false); load(); }
    };
    const reopen = async () => {
        const reason = await ask({ title: 'Reopen this settlement as a draft?', message: 'The approvals start again and the statement is withdrawn.', required: true, confirmText: 'Reopen' });
        if (reason === null) return;
        act(() => api.post(`/lifecycle/settlements/${id}/reopen`, { reason }), 'Reopened as a draft');
    };

    if (!s) {
        return (
            <div className="container" style={{ padding: '2rem 1rem', maxWidth: 900 }}>
                <PageHeader backTo="/tasks" backLabel="Back to My tasks" />
                <Message message={message} />
                {!message && <p style={muted}>Loading…</p>}
            </div>
        );
    }
    const [label, color] = STATUS[s.status] || [s.status, '#6b7280'];
    const earn = s.lines.filter(l => l.kind === 'EARNING' && l.counted);
    const ded = s.lines.filter(l => l.kind === 'DEDUCTION' && l.counted);
    const info = s.lines.filter(l => l.kind === 'INFO' || !l.counted);
    const showSteps = s.steps.length > 0;
    const blockers = s.warnings?.blockers || [];
    const notes = s.warnings?.notes || [];
    const net = s.netPayable;
    const rowProps = { s, editable: s.canEdit, onChanged: load, say };
    // The same place the footer link below goes: Joiners & leavers for someone with rights here, else My tasks.
    const canAct = s.canEdit || s.canReopen || s.canIssueLetters || s.canIssueStatement;
    const backTo = canAct ? '/admin/checklists' : '/tasks';
    const backLabel = canAct ? 'Back to Joiners and leavers' : 'Back to My tasks';

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 900 }}>
            {dialogs}
            <PageHeader backTo={backTo} backLabel={backLabel} />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                    <h1 style={{ marginBottom: 4 }}>Final settlement: {s.user.name}</h1>
                    <p style={{ ...muted, fontSize: 15, margin: 0 }}>
                        {s.exit.kindLabel}, last working day {longDate(s.exit.lastWorkingDay)}
                        {['PAID', 'CLOSED'].includes(s.status) ? '' : ` · to be settled by ${dateWithYear(s.dueDate)}${s.overdue ? ' (overdue)' : ''}`}
                        {s.status === 'CLOSED' ? ' · settled, and the account is closed' : ''}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span data-status={s.status} style={{ border: `1px solid ${color}`, color, borderRadius: 16, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>{label}</span>
                    <a style={ghost} href={`${api.defaults.baseURL || '/api'}/lifecycle/settlements/${s.id}/statement`} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Statement</a>
                </div>
            </div>
            <Message message={message} />

            {s.status === 'DRAFT' && blockers.length > 0 && (
                <section className="card" style={{ ...card, borderLeft: '4px solid #ef4444' }} role="alert">
                    <strong style={{ color: 'var(--text-main)' }}>To sort out before it can be sent for approval</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--text-main)' }}>{blockers.map(b => <li key={b}>{b}</li>)}</ul>
                </section>
            )}
            {!['PAID', 'CLOSED', 'CANCELLED'].includes(s.status) && notes.length > 0 && (
                <section className="card" style={{ ...card, borderLeft: '4px solid #d97706' }}>
                    <strong style={{ color: 'var(--text-main)' }}>Worth knowing <span style={{ ...muted, fontWeight: 400 }}>(as of the last calculation)</span></strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--text-main)' }}>{notes.map(n => <li key={n}>{n}</li>)}</ul>
                </section>
            )}

            <section className="card" style={card}>
                <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Paid to {s.user.name.split(' ')[0]}</h2>
                {earn.length === 0 ? <p style={muted}>Nothing under this heading.</p> : <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{earn.map(l => <LineRow key={`${l.id}-${l.amount}-${l.overrideReason}`} l={l} {...rowProps} />)}</ul>}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: rowBorder, padding: '8px 0', fontWeight: 600, color: 'var(--text-main)' }}><span>Total</span><span>{money(s.totalEarnings, s.currency)}</span></div>
                <h2 style={{ fontSize: 17, margin: '12px 0 4px' }}>Recovered from {s.user.name.split(' ')[0]}</h2>
                {ded.length === 0 ? <p style={muted}>Nothing is recovered.</p> : <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{ded.map(l => <LineRow key={`${l.id}-${l.amount}-${l.overrideReason}`} l={l} {...rowProps} />)}</ul>}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: rowBorder, padding: '8px 0', fontWeight: 600, color: 'var(--text-main)' }}><span>Total</span><span>{money(s.totalDeductions, s.currency)}</span></div>
                <div data-testid="net" style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--text-main)', borderBottom: '2px solid var(--text-main)', padding: '10px 0', fontSize: 18, fontWeight: 700, color: 'var(--text-main)' }}>
                    <span>{net > 0 ? 'Net payable to the employee' : net < 0 ? 'Net recoverable from the employee' : 'Nothing payable either way'}</span>
                    <span>{money(Math.abs(net), s.currency)}</span>
                </div>
                {info.length > 0 && <ul style={{ ...muted, margin: '10px 0 0', paddingLeft: 18 }}>{info.map(l => <li key={l.id}>{l.label}</li>)}</ul>}
                {s.canEdit && (
                    <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
                        <AddLine s={s} onChanged={load} say={say} />
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button style={ghost} disabled={busy} onClick={() => act(() => api.post(`/lifecycle/settlements/${id}/recompute`), 'Recalculated. Your changes and added lines were kept.')}><RefreshCw size={14} /> Recalculate</button>
                            <button className="btn btn-primary" style={primary} disabled={busy} onClick={() => act(() => api.post(`/lifecycle/settlements/${id}/submit`), 'Sent for approval')}><Send size={14} /> Send for approval</button>
                        </div>
                    </div>
                )}
            </section>

            {(showSteps || s.status === 'PENDING_APPROVAL') && (
                <section className="card" style={card}>
                    <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>Approval</h2>
                    <ExitSteps steps={s.steps} />
                    {s.canDecide && <div style={{ marginTop: 12 }}><Decide s={s} onChanged={load} say={say} /></div>}
                </section>
            )}

            {(s.canPay || s.canReopen || s.canIssueLetters || s.canIssueStatement || s.paidOn) && (
                <section className="card" style={{ ...card, display: 'grid', gap: 12 }}>
                    {s.paidOn && <p style={{ margin: 0, color: 'var(--text-main)' }}>{s.direction === 'RECOVERABLE' ? 'Recovered' : 'Paid'} on {dateWithYear(s.paidOn)} by {(MODES.find(m => m[0] === s.paymentMode) || [])[1] || s.paymentMode}{s.paymentRef ? `, reference ${s.paymentRef}` : ''}.</p>}
                    {s.canIssueStatement && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} role="alert">
                            <span style={{ color: '#d97706' }}>The statement has not been issued to the employee.</span>
                            <button style={ghost} disabled={busy} onClick={() => act(() => api.post(`/lifecycle/settlements/${id}/statement`), 'The statement was issued and emailed')}><FileText size={14} /> Issue the statement</button>
                        </div>
                    )}
                    {s.canPay && <Pay s={s} onChanged={load} say={say} />}
                    {s.canIssueLetters && <Letters s={s} onChanged={load} say={say} />}
                    {s.canReopen && <div><button style={ghost} disabled={busy} onClick={reopen}><RotateCcw size={14} /> Reopen as a draft</button></div>}
                </section>
            )}
            <p style={muted}>The final month's salary is paid in the normal payroll run. <Link to={backTo}>{backLabel}</Link></p>
        </div>
    );
};

export default SettlementPage;
